import React from "react";
import { useSelector, useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import { Card, CardContent, CircularProgress, Grid } from "@material-ui/core";
import CopyIcon from "@material-ui/icons/FileCopy";

import {
  TextInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  AutoCompleteInput,
  BackButton,
  WithReduxForm,
  PageHeader,
  PageFooter,
  Button,
  showNotification
} from "jazasoft";
import { required as mtRequired } from "@jazasoft/mui-table";

import { isEmpty, inputOptions, isTrue } from "../../../utils/helpers";
import { FabClassification } from "../../../constants";
import useMutation from "../../../hooks/useMutation";
import compressImage from "../../../utils/compressImage";
import MuiTable from "../../../components/MuiTable";
import useQuery from "../../../hooks/useQuery";
import useSetting from "../../../hooks/useSetting";
import SettingKeys from "../../../utils/SettingKeys";
import CustomInputElements from "../../../components/CustomInputElements";
import { FabricFields } from "../../../utils/entityFields";
import hasPrivilege from "../../../utils/hasPrivilege";
import ResourceId from "../../../utils/ResourceId";
import { Scope } from "../../../utils/privilege";
import Forbidden from "../../../components/Forbidden";
import getValidation from "../../../utils/getValidation";
import { useDeepEffect, useDeepMemo, useEntityPrivilege, useSafeState } from "../../../hooks";

export const choicesClassification = Object.values(FabClassification).map(v => ({ id: v, name: v }));

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3)
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "& > button": {
      marginRight: theme.spacing(2)
    }
  }
}));

const FabricEdit = props => {
  const classes = useStyles();
  const { id, i18nKey, history, basePath, resource, roles, hasAccess } = props;

  const { data: fabric = {} } = useQuery(id && { url: `fabrics/${id}` });
  const { data: fabricPatternList = [] } = useQuery({ url: `fabric-patterns`, params: { size: 2000 } });

  const { disableEdit } = useEntityPrivilege("fabric");
  const [rows, setRows] = useSafeState([]);
  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, resource);

  const formValues = useSelector(state => state?.form && state.form["record-form"]?.values);

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  const initialValues = {
    ...fabric,
    fabricCategoryId: fabric.fabricCategory?.id
  };

  const columns = useDeepMemo(() => {
    const columnList = [
      {
        dataKey: "color",
        title: "Fabric Color",
        inputType: "text-input",
        validate: [mtRequired()],
        disabled: row => isTrue(row.invoiceCreated),
        options: { fullWidth: true }
      },
      {
        dataKey: "code",
        title: "Fabric Color Code",
        inputType: "text-input",
        // validate: [mtRequired()],
        disabled: row => isTrue(row.invoiceCreated),
        options: { fullWidth: true }
      },
      {
        dataKey: "fabricPatternId",
        title: "Fabric Pattern",
        inputType: "select-input",
        choices: fabricPatternList || [],
        // validate: [mtRequired()],
        // disabled: row => isTrue(row.invoiceCreated),
        options: { fullWidth: true }
      },
      {
        dataKey: "repeatLength",
        title: "Repeat - Length (CM)",
        inputType: "text-input",
        // validate: [mtRequired()],
        // disabled: row => isTrue(row.invoiceCreated),
        options: { fullWidth: true }
      },
      {
        dataKey: "repeatWidth",
        title: "Repeat - Width (CM)",
        inputType: "text-input",
        // validate: [mtRequired()],
        // disabled: row => isTrue(row.invoiceCreated),
        options: { fullWidth: true }
      },

      {
        dataKey: "swatchImage",
        title: "Swatch Image",
        inputType: "file-input",
        options: {
          accept: { "image/png": [".png"], "image/jpeg": [".jpeg", ".jpg"], "image/gif": [".gif"] }
        }
      }
    ];
    return columnList;
  }, [fabricPatternList?.length]);

  useDeepEffect(() => {
    if (!isEmpty(fabric) || !isEmpty(fabric.colorList)) {
      setRows(fabric.colorList?.map(row => ({ ...row })));
    }
  }, [fabric]);

  const onSave = event => {
    ffHandleSubmit(event);
  };

  const onSubmit = async (rows, _, onSubmitComplete) => {
    //** Extra Validation for Stripes and Checks Fabric Patterns */
    let resourcesError = "resources.fabrics.errors.";
    let addedColors = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (isEmpty(row?.color)) continue;

      const color = `${row.color?.trim()}_${row.code?.trim()}`;
      
      if (!addedColors.some(e => color === e)) {
        addedColors.push(color);
      } else {
        dispatch(showNotification(`Duplicate Color: ${row.color}, Code: ${row.code}`, { type: "danger" }));
        onSubmitComplete && onSubmitComplete(rows);
        return;
      }

      if (row.fabricPatternId) {
        const fabricPattern = fabricPatternList?.find(e => e.id === row?.fabricPatternId);
        if (fabricPattern && fabricPattern.name === "Stripes") {
          if (!(row.repeatLength || row.repeatWidth)) {
            dispatch(showNotification(resourcesError + "repeat_length_required", { type: "danger" }));
            return;
          }
          if (row.repeatLength && row.repeatWidth) {
            dispatch(showNotification(resourcesError + "repeat_in_only_one_direction", { type: "danger" }));
            return;
          }
        }
        if (fabricPattern && fabricPattern.name === "Checks") {
          if (isEmpty(row.repeatLength)) {
            dispatch(showNotification(resourcesError + "repeat_length_required", { type: "danger" }));
            return;
          }
          if (isEmpty(row.repeatWidth)) {
            dispatch(showNotification(resourcesError + "repeat_width_required", { type: "danger" }));
            return;
          }
        }
      } else {
        if (row.repeatLength || row.repeatWidth) {
          dispatch(showNotification("Fabric Pattern is Required if Repeat Length or Width is given", { type: "danger" }));
          onSubmitComplete && onSubmitComplete(rows);
          return;
        }
      }
    }

    const data = { ...formValues };
    const colorList = rows
      .filter(e => !isEmpty(e.color))
      .map((e, idx) => ({
        id: e.id,
        color: e.color?.trim(),
        code: e.code?.trim(),
        fabricPatternId: e.fabricPatternId,
        repeatLength: e.repeatLength,
        repeatWidth: e.repeatWidth,
        swatchImage: e.swatchImage?.name
      }));
    const swatchImageFiles = rows.filter(e => e.swatchImage instanceof File).map(e => e.swatchImage);
    const compressedFiles = await Promise.all(
      swatchImageFiles.map(file => {
        const size = Math.round((file?.size || 0) / (1024 * 1024));
        const quality = size > 5 ? 0.2 : size > 3 ? 0.3 : 0.4;
        return compressImage(file, quality);
      })
    );

    const body = { ...data, colorList };
    const formData = new FormData();
    formData.append("body", JSON.stringify(body));
    compressedFiles.forEach(swatchImageFile => formData.append("swatchImages", swatchImageFile));

    const options = {
      url: `fabrics/${id}/multipart`,
      method: "put",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    };

    mutate(options, {
      onSuccess: data => {
        history.push(`${basePath}/${data?.id}/view`);
        onSubmitComplete && onSubmitComplete();
      },
      onFailure: () => onSubmitComplete && onSubmitComplete()
    });
  };

  const customFields = fields?.filter(f => f.startsWith("customField")) || [];

  const toolbarActions = [{ name: "copy", tooltip: "Copy", icon: <CopyIcon />, showLabel: true, options: { variant: "outlined", color: "primary" } }];

  const onToolbarActionClick = (event, action, rows) => {
    if (!isEmpty(rows)) {
      const fabricPatternId = rows[0]?.fabricPatternId;
      const repeatLength = rows[0]?.repeatLength;
      const repeatWidth = rows[0]?.repeatWidth;
      setRows(rows.map(e => ({ ...e, fabricPatternId, repeatLength, repeatWidth })));
    }
  };

  if (!hasPrivilege(roles, hasAccess, ResourceId.FABRIC, Scope.UPDATE) || disableEdit) {
    return <Forbidden history={history} />;
  }

  return (
    <div>
      {/** Header Element */}
      <PageHeader title="resources.fabrics.pageHeader.edit" titleArgs={{ _: "Update Fabric" }} />

      {/* Main Content */}
      <div className={classes.content}>
        <WithReduxForm initialValues={initialValues}>
          {() => (
            <div>
              {/* Basic Details */}
              <Card className={classes.marginVertical}>
                <CardContent>
                  <Grid container spacing={2}>
                    {fields.includes(FabricFields.ITEM_CODE) && (
                      <Grid item xs={12} sm={3}>
                        <TextInput
                          i18nKey={i18nKey}
                          source="itemCode"
                          validate={getValidation(validationRule[FabricFields.ITEM_CODE])}
                          disabled={isTrue(fabric.invoiceCreated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    {fields.includes(FabricFields.ITEM_DESCRIPTION) && (
                      <Grid item xs={12} sm={3}>
                        <TextInput
                          i18nKey={i18nKey}
                          source="itemDesc"
                          validate={getValidation(validationRule[FabricFields.ITEM_DESCRIPTION])}
                          disabled={isTrue(fabric.invoiceCreated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    {fields.includes(FabricFields.ARTICLE_NAME) && (
                      <Grid item xs={12} sm={3}>
                        <TextInput
                          i18nKey={i18nKey}
                          source="articleName"
                          validate={getValidation(validationRule[FabricFields.ARTICLE_NAME])}
                          disabled={isTrue(fabric.invoiceCreated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    {fields.includes(FabricFields.CLASSIFICATION) && (
                      <Grid item xs={12} sm={3}>
                        <SelectInput
                          i18nKey={i18nKey}
                          source="classification"
                          choices={choicesClassification}
                          validate={getValidation(validationRule[FabricFields.CLASSIFICATION])}
                          disabled={isTrue(fabric.invoiceCreated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    {fields.includes(FabricFields.FABRIC_CATEGORY_ID) && (
                      <Grid item xs={12} sm={3}>
                        <ReferenceInput
                          i18nKey={i18nKey}
                          source="fabricCategoryId"
                          reference="fabric-categories"
                          validate={getValidation(validationRule[FabricFields.FABRIC_CATEGORY_ID])}
                          disabled={isTrue(fabric.cutPlanGenerated)}
                          {...inputOptions(3)}>
                          <AutoCompleteInput />
                        </ReferenceInput>
                      </Grid>
                    )}
                    {fields.includes(FabricFields.CONSTRUCTION) && (
                      <Grid item xs={12} sm={3}>
                        <TextInput
                          i18nKey={i18nKey}
                          source="construction"
                          validate={getValidation(validationRule[FabricFields.CONSTRUCTION])}
                          disabled={isTrue(fabric.cutPlanGenerated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    {fields.includes(FabricFields.CONTENT) && (
                      <Grid item xs={12} sm={3}>
                        <TextInput
                          i18nKey={i18nKey}
                          source="content"
                          validate={getValidation(validationRule[FabricFields.CONTENT])}
                          disabled={isTrue(fabric.cutPlanGenerated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    {fields.includes(FabricFields.GSM) && (
                      <Grid item xs={12} sm={3}>
                        <NumberInput
                          i18nKey={i18nKey}
                          source="gsm"
                          validate={getValidation(validationRule[FabricFields.GSM])}
                          disabled={isTrue(fabric.cutPlanGenerated)}
                          {...inputOptions(3)}
                        />
                      </Grid>
                    )}
                    <CustomInputElements fields={customFields} validationRule={validationRule} i18nKey={i18nKey} />
                  </Grid>
                </CardContent>
              </Card>

              <Card className={classes.marginVertical}>
                <div>
                  <MuiTable
                    title="Fabric Colors"
                    columns={columns}
                    rows={rows}
                    component="div"
                    editable
                    selectable
                    editing
                    showEditableActions
                    toolbarActions={toolbarActions}
                    onToolbarActionClick={onToolbarActionClick}
                    rowAddCount={1}
                    handleSubmitRef={handleSubmitRef}
                    onSubmit={onSubmit}
                    fontSize={16}
                    cellLength={150}
                    i18nKey="resources.fabrics.fields.color"
                  />
                </div>
              </Card>

              {/* Footer */}
              <PageFooter className={classes.pageFooter}>
                <Button label="Save" color="primary" variant="contained" disabled={saving} onClick={onSave}>
                  {saving && <CircularProgress size={20} thickness={2} />}
                </Button>
                <BackButton label="Back" variant="outlined" color="primary" />
              </PageFooter>
            </div>
          )}
        </WithReduxForm>
      </div>
    </div>
  );
};

export default FabricEdit;
